import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {APIResponseStatus, RoleId} from 'common/types';
import {useCurrentUser} from 'hooks/user';
import {activityFeedAPI} from 'services/ActivityFeedAPI';
import {ACTIVITY_FEED_PUSH_OPENED, ACTIVITY_FEED_PUSH_RECEIVED} from 'common/nativePush';

interface FeedState {
  count: number;
  tick: number;
  pushTick: number;
  markRead: (ids: number[]) => Promise<number[]>;
  openFeed: (snapshotId: number) => Promise<number | null>;
}
const FeedContext = createContext<FeedState>({count: 0, tick: 0, pushTick: 0, markRead: async () => [], openFeed: async () => null});
export const useActivityFeed = () => useContext(FeedContext);

// Mounted once around the layout, shared by desktop, drawer, mobile and feed.
export const ActivityFeedProvider = ({children}: {children: React.ReactNode}) => {
  const user = useCurrentUser();
  const userId = user && user.roleId === RoleId.Realtor ? user.userId : null;
  const [count, setCount] = useState(0);
  const [tick, setTick] = useState(0);
  const [pushTick, setPushTick] = useState(0);
  const acknowledged = useRef(new Set<number>());
  const pending = useRef(new Set<number>());
  const generation = useRef(0);
  const request = useRef(0);
  const initialReadPending = useRef<Promise<number | null> | null>(null);
  const initialReadDone = useRef(false);
  const readThrough = useRef<number | null>(null);

  useEffect(() => {
    const version = ++generation.current;
    acknowledged.current.clear();
    pending.current.clear();
    initialReadPending.current = null;
    initialReadDone.current = false;
    readThrough.current = null;
    setCount(0);
    if (!userId) { return; }
    let fetching = false;
    let refreshQueued = false;
    let debounceTimer: number | undefined;
    let maxWaitTimer: number | undefined;
    const refresh = async () => {
      if (version !== generation.current) { return; }
      if (fetching) { refreshQueued = true; return; }
      if (pending.current.size || initialReadPending.current) { return; }
      fetching = true;
      const serial = ++request.current;
      try {
        const response = await activityFeedAPI.unread();
        if (response.status !== APIResponseStatus.Ok) { throw new Error('Activity feed request failed'); }
        if (version === generation.current && serial === request.current) {
          setCount(response.data.count);
        }
      } catch (error) {
        console.debug('Activity feed unread refresh failed', error);
      } finally {
        fetching = false;
        if (refreshQueued) { refreshQueued = false; refresh(); }
      }
    };
    const flushReceipt = () => {
      window.clearTimeout(debounceTimer);
      window.clearTimeout(maxWaitTimer);
      debounceTimer = undefined;
      maxWaitTimer = undefined;
      window.__pendingActivityFeedRefresh = false;
      refresh();
      setPushTick(value => value + 1);
    };
    const receivePush = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(flushReceipt, 2000);
      if (maxWaitTimer === undefined) { maxWaitTimer = window.setTimeout(flushReceipt, 5000); }
    };
    window.addEventListener(ACTIVITY_FEED_PUSH_RECEIVED, receivePush);
    // A tap on the open feed refreshes immediately and consumes a pending receipt.
    window.addEventListener(ACTIVITY_FEED_PUSH_OPENED, flushReceipt);
    if (window.__pendingActivityFeedRefresh) { receivePush(); }
    refresh();
    const timer = window.setInterval(() => {
      refresh();
      setTick(value => value + 1);
    }, 60000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(debounceTimer);
      window.clearTimeout(maxWaitTimer);
      window.removeEventListener(ACTIVITY_FEED_PUSH_RECEIVED, receivePush);
      window.removeEventListener(ACTIVITY_FEED_PUSH_OPENED, flushReceipt);
      window.__pendingActivityFeedRefresh = false;
      ++generation.current;
    };
  }, [userId]);

  const openFeed = useCallback(async (snapshotId: number) => {
    if (initialReadDone.current) { return readThrough.current; }
    if (initialReadPending.current) { return initialReadPending.current; }
    const version = generation.current;
    const serial = ++request.current;
    const opening = (async () => {
      const response = await activityFeedAPI.open(snapshotId);
      if (response.status !== APIResponseStatus.Ok) { throw new Error('Activity feed request failed'); }
      if (version !== generation.current) { throw new Error('Activity feed user changed'); }
      initialReadDone.current = true;
      readThrough.current = response.data.readThroughId;
      if (serial === request.current) { setCount(response.data.count); }
      return response.data.readThroughId;
    })();
    initialReadPending.current = opening;
    try { return await opening; }
    finally { if (version === generation.current) { initialReadPending.current = null; } }
  }, []);

  const markRead = useCallback(async (ids: number[]) => {
    const known = ids.filter(id => acknowledged.current.has(id));
    const fresh = ids.filter(id => !acknowledged.current.has(id) && !pending.current.has(id));
    if (!fresh.length) { return known; }
    const version = generation.current;
    const serial = ++request.current;
    fresh.forEach(id => pending.current.add(id));
    try {
      const response = await activityFeedAPI.read(fresh);
      if (response.status !== APIResponseStatus.Ok) { throw new Error('Activity feed request failed'); }
      if (version !== generation.current) { return []; }
      fresh.forEach(id => acknowledged.current.add(id));
      if (serial === request.current) { setCount(response.data.count); }
      return known.concat(fresh);
    } finally {
      if (version === generation.current) { fresh.forEach(id => pending.current.delete(id)); }
    }
  }, []);

  return <FeedContext.Provider value={{count, tick, pushTick, markRead, openFeed}}>{children}</FeedContext.Provider>;
};
